/**
 * StickyBottomCta - Sticky bottom CTA bar
 *
 * - Dark blue background (#2f3d7e)
 * - "Build the skills that move your career forward" text on left
 * - White "SIGN UP" button on right
 * - Full width on mobile, max 600px on desktop
 */
import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useTranslation } from '@mc/i18n'
import { useEnrollmentCtaConfig } from '../../../hooks/useEnrollmentCtaConfig'
import { useFireEvent } from '../../../tracking/educatorTracking'

export type StickyAnchor = { readonly current: HTMLElement | null } | string

interface StickyBottomCtaProps {
  showAfter: StickyAnchor[]
  hideWhen?: StickyAnchor[]
}

const NO_ANCHORS: StickyAnchor[] = []

const resolveAnchor = (anchor: StickyAnchor): HTMLElement | null =>
  typeof anchor === 'string'
    ? document.querySelector<HTMLElement>(anchor)
    : anchor.current

const resolveAll = (anchors: StickyAnchor[]): HTMLElement[] =>
  anchors.map(resolveAnchor).filter((el): el is HTMLElement => el !== null)

export const StickyBottomCta = ({
  showAfter,
  hideWhen = NO_ANCHORS,
}: StickyBottomCtaProps) => {
  const { t } = useTranslation('@mc/educator')
  const fireEvent = useFireEvent()
  const { primaryCta, isPrimaryLogIn } = useEnrollmentCtaConfig()
  const [isVisible, setIsVisible] = useState(false)
  const hasTrackedRowViewed = useRef(false)

  useEffect(() => {
    if (isVisible && !hasTrackedRowViewed.current) {
      fireEvent('educatorRowViewed', {
        row_name: 'sticky footer',
        row_position_index: 1,
      })
      hasTrackedRowViewed.current = true
    }
  }, [isVisible, fireEvent])

  const anchorsRef = useRef({ showAfter, hideWhen })
  anchorsRef.current = { showAfter, hideWhen }

  useEffect(() => {
    const handleScroll = () => {
      const gates = resolveAll(anchorsRef.current.showAfter)
      if (!gates.length) return

      const isPastAll = gates.every(
        (el) => el.getBoundingClientRect().bottom < 0,
      )
      const isSuppressed = resolveAll(anchorsRef.current.hideWhen).some(
        (el) => el.getBoundingClientRect().top < window.innerHeight,
      )

      setIsVisible(isPastAll && !isSuppressed)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-50 flex justify-center transition-all duration-300 md:bottom-[20px] ${
        isVisible
          ? 'translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-full opacity-0'
      }`}
    >
      <div
        className='flex w-full items-center gap-4 px-5 pb-[calc(15px+env(safe-area-inset-bottom))] pt-[15px] md:max-w-[600px] md:pb-[15px]'
        style={{ backgroundColor: '#2f3d7e' }}
      >
        <span
          className='flex-1 text-[16px] leading-[1.2] tracking-[0.48px]'
          style={{ color: '#ffffff' }}
        >
          {t('enrollment.stickyCta.text')}
        </span>

        <Link
          href={primaryCta.href}
          onClick={() => {
            if (!isPrimaryLogIn) {
              fireEvent('educatorPurchaseClick', {
                cta_location: 'sticky footer',
                cta: primaryCta.label,
              })
            }
            fireEvent('educatorSignClick', {
              action: primaryCta.label.toLowerCase(),
              location: 'sticky footer',
            })
          }}
          className='flex h-[40px] items-center justify-center px-[30px] py-[15px] text-[14px] font-semibold uppercase leading-[1.2] tracking-[0.7px]'
          style={{
            backgroundColor: '#ffffff',
            color: '#2f3d7e',
          }}
        >
          {primaryCta.label}
        </Link>
      </div>
    </div>
  )
}
